import React, { useEffect, useState } from 'react';

/**
 * GeoGate — soft block for visitors from specified countries.
 *
 * Detection: ipapi.co (free, no key, 30k req/month — plenty for a soft gate).
 * Caching: result stashed in localStorage for 24h so we don't re-probe on
 *          every navigation. Failure to detect → fail-open (assume allowed).
 * Bypass: ?gate-pass=<BYPASS_TOKEN> sets a permanent localStorage flag so the
 *         admin can still hit /admin/config from a blocked country.
 *
 * This is intentionally a "soft" block. A determined visitor with a VPN
 * or JS disabled can bypass it — that's by design per product spec.
 */

// Countries to block. ISO 3166-1 alpha-2 codes, uppercase.
const BLOCKED_COUNTRIES = new Set(['IN']);

const CACHE_KEY = 'va_geo_v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const BYPASS_KEY = 'va_geo_bypass';
// This is NOT a security boundary — the geo gate itself is bypassable by VPN
// by design. Kept as a distinct, non-secret token so it can safely ship in
// the public JS bundle without exposing the admin password.
const BYPASS_TOKEN = 'va-geo-bypass-v1';

function readCachedCountry() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { country, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return country || null;
  } catch {
    return null;
  }
}

function writeCachedCountry(country) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ country, ts: Date.now() }));
  } catch {
    // localStorage disabled / full — ignore, gate will just re-probe next load
  }
}

async function detectCountry() {
  try {
    const cached = readCachedCountry();
    if (cached) return cached;
    const res = await fetch('https://ipapi.co/json/', {
      // Don't send credentials and don't follow oddly-redirected responses.
      credentials: 'omit',
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    const country = (data?.country_code || data?.country || '').toUpperCase();
    if (country) writeCachedCountry(country);
    return country || null;
  } catch {
    return null;
  }
}

function checkBypass() {
  try {
    const params = new URLSearchParams(window.location.search);
    const passed = params.get('gate-pass');
    if (passed && passed === BYPASS_TOKEN) {
      localStorage.setItem(BYPASS_KEY, '1');
      return true;
    }
    return localStorage.getItem(BYPASS_KEY) === '1';
  } catch {
    return false;
  }
}

export default function GeoGate({ children }) {
  // 'loading' | 'allowed' | 'blocked'
  const [state, setState] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (checkBypass()) {
        if (!cancelled) setState('allowed');
        return;
      }
      const country = await detectCountry();
      if (cancelled) return;
      if (country && BLOCKED_COUNTRIES.has(country)) {
        setState('blocked');
      } else {
        // Fail-open: null country (network error, ad-blocker) → allow.
        setState('allowed');
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  if (state === 'loading') {
    // Render nothing — keep the page blank for the ~200ms detection window
    // so blocked visitors don't briefly see the real app underneath.
    return (
      <div
        className="fixed inset-0 bg-[#0F1419]"
        aria-hidden="true"
        data-testid="geo-gate-loading"
      />
    );
  }

  if (state === 'blocked') {
    return (
      <div
        className="fixed inset-0 z-[9999] bg-[#0F1419] text-white flex items-center justify-center px-6"
        data-testid="geo-gate-blocked"
      >
        <div className="max-w-xl text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FFB238]/10 mb-6">
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-[#FFB238]" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M4.93 4.93l14.14 14.14" />
            </svg>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Visual Atmos</h1>
          <p className="text-lg text-gray-300 leading-relaxed">
            Sorry, but our service is currently unavailable in your country.
          </p>
          <p className="text-sm text-gray-500 mt-8">
            If you believe this is a mistake, please{' '}
            <a
              href="mailto:support@visualatmos.com"
              className="text-[#FFB238] hover:underline"
            >
              contact support
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return children;
}
